import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) { const dt = new Date(ms); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};
const fmt2Date = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const SalesManCommissionSummary = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [executives, setExecutives] = useState([]);
  const [salesManId, setSalesManId] = useState('');
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    if (companyId && token) {
      fetchData('SalesExecutiveApi', `/${companyId}`, token)
        .then(res => setExecutives(res?.response_data || res || []))
        .catch(() => setExecutives([]));
      fetchData('CategoryApi', `/${companyId}`, token)
        .then(res => setAllCategories(res?.response_data || res || []))
        .catch(() => setAllCategories([]));
    }
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const smParam = salesManId ? `${salesManId}` : '0';
      const res = await fetchData('SaleReportApi', `/SalesmanCommissionDetails/${companyId}/${branchId}/${smParam}/${fromDate}/${toDate}`, token);
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch salesman commission summary');
    } finally {
      setLoading(false);
    }
  };

  // Build pivot: rows = customers, columns = ALL categories (from CategoryApi)
  const { salesmen, customers, pivot } = useMemo(() => {
    if (!data.length) return { salesmen: allCategories.map(c => c.Name), customers: [], pivot: {} };

    const custMap = {};
    const piv = {};

    data.forEach(r => {
      const cust = r.CustomerName || '(Unknown)';
      const cat  = r.CategoryName || '(Unknown)';
      if (!custMap[cust]) custMap[cust] = cust;
      if (!piv[cust])     piv[cust]     = {};
      if (!piv[cust][cat]) piv[cust][cat] = { qty: 0, commission: 0, qtyModeQty: 0, amtModeAmt: 0 };
      piv[cust][cat].qty        += parseFloat(r.Quantity)        || 0;
      piv[cust][cat].commission += parseFloat(r.CommissionAmount)|| 0;
      if ((r.CommissionMode || '').toUpperCase() === 'QTY') {
        piv[cust][cat].qtyModeQty += parseFloat(r.Quantity) || 0;
      } else {
        piv[cust][cat].amtModeAmt += parseFloat(r.TaxableAmount) || 0;
      }
    });

    return {
      salesmen:  allCategories.map(c => c.Name),  // all categories as columns
      customers: Object.keys(custMap),
      pivot:     piv,
    };
  }, [data, allCategories]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SalesManCommissionSummary', 0);
    if (!customers.length) return;

    // Header row 1: salesman names spanning 2 cols each
    const h1 = ['Customer', ...salesmen.flatMap(sm => [sm, '']), 'Total Qty', 'Total Commission'];
    // Header row 2: Qty / Commission sub-headers
    const h2 = ['', ...salesmen.flatMap(() => ['Qty', 'Commission']), '', ''];

    const sheetRows = [h1, h2];

    customers.forEach(cust => {
      let tQty = 0, tComm = 0;
      const cells = salesmen.flatMap(sm => {
        const v = pivot[cust]?.[sm];
        const q = v?.qty || 0; const c = v?.commission || 0;
        tQty += q; tComm += c;
        return [q ? fmtN(q) : '', c ? fmtN(c) : ''];
      });
      sheetRows.push([cust, ...cells, fmtN(tQty), fmtN(tComm)]);
    });

    // Totals row
    let gQty = 0, gComm = 0;
    const totCells = salesmen.flatMap(sm => {
      const q  = customers.reduce((s, c) => s + (pivot[c]?.[sm]?.qty        || 0), 0);
      const co = customers.reduce((s, c) => s + (pivot[c]?.[sm]?.commission || 0), 0);
      gQty += q; gComm += co;
      return [fmtN(q), fmtN(co)];
    });
    sheetRows.push(['Totals', ...totCells, fmtN(gQty), fmtN(gComm)]);

    await exportStyledExcel({
      reportTitle: 'SALESMAN COMMISSION SUMMARY',
      sheetName: 'Commission Summary',
      fileName: `SalesManCommission_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      rawRows: sheetRows,
    });
  };

  // Grand totals per salesman
  const smTotals = useMemo(() => {
    const t = {};
    salesmen.forEach(sm => {
      t[sm] = { qty: 0, commission: 0, amtModeAmt: 0 };
      customers.forEach(c => {
        t[sm].qty        += pivot[c]?.[sm]?.qty        || 0;
        t[sm].commission += pivot[c]?.[sm]?.commission || 0;
        t[sm].amtModeAmt += pivot[c]?.[sm]?.amtModeAmt || 0;
      });
    });
    return t;
  }, [salesmen, customers, pivot]);


  const grandQty  = salesmen.reduce((s, sm) => s + (smTotals[sm]?.qty        || 0), 0);
  const grandComm = salesmen.reduce((s, sm) => s + (smTotals[sm]?.commission || 0), 0);

  // cell styles
  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #c8cfe8', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading commission summary..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Salesman Commission Summary</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <SearchableDropdown
            value={salesManId}
            onChange={v => setSalesManId(v)}
            options={[{ value: '', label: '— All Salesman —' }, ...executives.map(e => ({ value: String(e.Id), label: e.Name }))]}
            placeholder="— All Salesman —"
            minWidth={160}
            primaryColor={PRIMARY}
          />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!customers.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Meta info */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
            {fromDate} to {toDate} &nbsp;|&nbsp; {customers.length} customers &nbsp;|&nbsp; {salesmen.length} categories
          </div>
        )}

        {/* Pivot Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 140px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              {/* Row 1: Category names */}
              <tr>
                <th rowSpan={2} style={{ ...thBase, minWidth: 180, textAlign: 'left', verticalAlign: 'middle', zIndex: 4, position: 'sticky', left: 0 }}>
                  Customer
                </th>
                {salesmen.map(sm => (
                  <th key={sm} colSpan={3} style={{ ...thBase, textAlign: 'center', background: '#2d3494', borderBottom: '1px solid rgba(255,255,255,0.3)' }}>
                    {sm}
                  </th>
                ))}
                <th colSpan={2} style={{ ...thBase, textAlign: 'center', background: '#0f4c81' }}>
                  Total
                </th>
              </tr>
              {/* Row 2: QTY | AMT | Commission sub-headers */}
              <tr>
                {salesmen.map(sm => (
                  <>
                    <th key={`${sm}-qty`}  style={{ ...thBase, textAlign: 'right', background: '#3d4db5', fontSize: 10 }}>QTY</th>
                    <th key={`${sm}-amt`}  style={{ ...thBase, textAlign: 'right', background: '#3d4db5', fontSize: 10 }}>AMT</th>
                    <th key={`${sm}-comm`} style={{ ...thBase, textAlign: 'right', background: '#3d4db5', fontSize: 10 }}>Commission</th>
                  </>
                ))}
                <th style={{ ...thBase, textAlign: 'right', background: '#1565c0', fontSize: 10 }}>Qty</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#1565c0', fontSize: 10 }}>Commission</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={1 + salesmen.length * 2 + 2} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click Refresh'}
                  </td>
                </tr>
              ) : (
                customers.map((cust, i) => {
                  let rowQty = 0, rowComm = 0;
                  const cells = salesmen.map(sm => {
                    const v = pivot[cust]?.[sm];
                    const q = v?.qty || 0; const c = v?.commission || 0;
                    rowQty += q; rowComm += c;
                    return { q, c, qtyModeQty: v?.qtyModeQty || 0, amtModeAmt: v?.amtModeAmt || 0 };
                  });
                  return (
                    <tr key={cust} style={{ background: i % 2 === 0 ? '#fff' : '#f0f4ff' }}>
                      <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY, position: 'sticky', left: 0, background: i % 2 === 0 ? '#fff' : '#f0f4ff', minWidth: 180 }}>
                        {cust}
                      </td>
                      {cells.map((v, ci) => (
                        <>
                          <td key={`q${ci}`}  style={{ ...tdBase, textAlign: 'right', color: '#374151' }}>{v.qtyModeQty ? fmtN(v.qtyModeQty) : ''}</td>
                          <td key={`a${ci}`}  style={{ ...tdBase, textAlign: 'right', color: '#374151' }}>{v.amtModeAmt ? fmtN(v.amtModeAmt) : ''}</td>
                          <td key={`c${ci}`}  style={{ ...tdBase, textAlign: 'right', color: v.c ? '#059669' : '#ccc', fontWeight: v.c ? 600 : 400 }}>{v.c ? fmtN(v.c) : ''}</td>
                        </>
                      ))}
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, background: '#e8f4fd' }}>{rowQty ? fmtN(rowQty) : ''}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669', background: '#e8f4fd' }}>{rowComm ? fmtN(rowComm) : ''}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {customers.length > 0 && (
              <tfoot>
                <tr style={{ background: '#dbeafe', fontWeight: 700 }}>
                  <td style={{ ...tdBase, fontWeight: 700, color: PRIMARY, position: 'sticky', left: 0, background: '#dbeafe' }}>Totals</td>
                  {salesmen.map(sm => (
                    <>
                      <td key={`tq-${sm}`} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{smTotals[sm]?.qty ? fmtN(smTotals[sm].qty) : ''}</td>
                      <td key={`ta-${sm}`} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{smTotals[sm]?.amtModeAmt ? fmtN(smTotals[sm].amtModeAmt) : ''}</td>
                      <td key={`tc-${sm}`} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{smTotals[sm]?.commission ? fmtN(smTotals[sm].commission) : ''}</td>
                    </>
                  ))}
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY, background: '#bfdbfe' }}>{fmtN(grandQty)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669', background: '#bfdbfe' }}>{fmtN(grandComm)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesManCommissionSummary;



